/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/RowContextMenu.tsx
 * FILE_ID: QB-SCOPESHEET-ROWCTX-001
 * PURPOSE: Right-click context menu on any line-item row — Excel parity for
 *          insert above / insert below / duplicate / clear values / delete.
 * WHY: User feedback: "I want the ease and flexibility like Excel, not having
 *      to click so many things." Right-click → row ops is the universal
 *      shortcut estimators expect from Excel and Google Sheets.
 * FLOW:
 *   ┌──────────────┐    ┌──────────────────┐    ┌──────────────────────┐
 *   │ Right-click  │ -> │ Floating menu at │ -> │ onAction(actionId)   │
 *   │ a row        │    │ click coords     │    │ → grid mutation      │
 *   └──────────────┘    └──────────────────┘    └──────────────────────┘
 */

import { useEffect, useRef } from 'react'
import { ArrowUpToLine, ArrowDownToLine, Copy, Eraser, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type RowContextAction =
  | 'insert-above'
  | 'insert-below'
  | 'duplicate'
  | 'clear-values'
  | 'delete'

interface RowContextMenuProps {
  /** Mouse coordinates the user right-clicked at — menu floats here. */
  x: number
  y: number
  /** Callback when an action is selected. Caller is responsible for the work. */
  onAction: (action: RowContextAction) => void
  /** Close the menu without picking an action (outside click / Esc). */
  onClose: () => void
  /** When true, hide insert/duplicate (read-only quote). */
  readOnly?: boolean
}

const MENU_ITEMS: {
  id: RowContextAction
  label: string
  icon: typeof ArrowUpToLine
  shortcut?: string
  destructive?: boolean
  hideWhenReadOnly?: boolean
}[] = [
  {
    id: 'insert-above',
    label: 'Insert row above',
    icon: ArrowUpToLine,
    shortcut: '⌘⇧+',
    hideWhenReadOnly: true,
  },
  {
    id: 'insert-below',
    label: 'Insert row below',
    icon: ArrowDownToLine,
    shortcut: '⌘+',
    hideWhenReadOnly: true,
  },
  {
    id: 'duplicate',
    label: 'Duplicate row',
    icon: Copy,
    shortcut: '⌘D',
    hideWhenReadOnly: true,
  },
  {
    id: 'clear-values',
    label: 'Clear values',
    icon: Eraser,
    shortcut: 'Del',
    hideWhenReadOnly: true,
  },
  {
    id: 'delete',
    label: 'Delete row',
    icon: Trash2,
    shortcut: '⌘⌫',
    destructive: true,
    hideWhenReadOnly: true,
  },
]

export default function RowContextMenu({ x, y, onAction, onClose, readOnly }: RowContextMenuProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  // Outside click + Esc dismissal — same pattern as ColumnHeaderMenu.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node)) return
      onClose()
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  // Clamp the menu to the viewport so it never paints off-screen — Excel-like
  // right-click near the bottom-right corner mustn't shoot off the page.
  const menuWidth = 220
  const menuHeight = 220
  const adjustedX =
    typeof window !== 'undefined' && x + menuWidth > window.innerWidth
      ? window.innerWidth - menuWidth - 8
      : x
  const adjustedY =
    typeof window !== 'undefined' && y + menuHeight > window.innerHeight
      ? window.innerHeight - menuHeight - 8
      : y

  const visibleItems = readOnly
    ? MENU_ITEMS.filter((m) => !m.hideWhenReadOnly)
    : MENU_ITEMS

  if (visibleItems.length === 0) return null

  return (
    <div
      ref={ref}
      className="fixed z-50 w-56 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl py-1 text-slate-700 dark:text-slate-200"
      style={{ top: adjustedY, left: adjustedX }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {visibleItems.map((m) => {
        const Icon = m.icon
        return (
          <button
            key={m.id}
            onClick={() => {
              onAction(m.id)
              onClose()
            }}
            className={cn(
              'w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[12px] hover:bg-slate-100 dark:hover:bg-slate-800',
              m.destructive && 'text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20',
            )}
          >
            <div className="flex items-center gap-2">
              <Icon className="w-3.5 h-3.5" />
              <span>{m.label}</span>
            </div>
            {m.shortcut && (
              <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">
                {m.shortcut}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
